// EmailBanner.tsx — migration banner shown at the top of authenticated
// pages when the user's email needs attention.
//
// Three states (in priority order):
//   1. No email on record → "Add email" CTA (opens ChangeEmailModal)
//   2. Email present + not verified → "Verify email" CTA (opens VerifyEmailModal)
//   3. Email present + verified → no banner (returns null)
//
// Styled with the warm account-flow design tokens so it sits visually
// adjacent to (but distinct from) the existing PrimeTime header.
// Dismissible per session via local component state — re-appears on
// the next page load if the underlying state still applies.

import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/app/store';

import FlexRow from '@/components/Common/FlexRow';
import { VerifyEmailModal, ChangeEmailModal } from '@/pages/Account/EmailModals';
import { cs } from '@/pages/Account/accountStyle';

const EmailBanner = () => {
  const { t } = useTranslation();
  const logins = useAppSelector((state) => state.logins);
  const desc = logins.login;

  const [dismissed, setDismissed] = React.useState(false);
  const [openVerify, setOpenVerify] = React.useState(false);
  const [openChange, setOpenChange] = React.useState(false);

  if (!desc || dismissed) return null;

  const hasEmail = !!desc.email;
  const isVerified = !!desc.email_verified;
  if (hasEmail && isVerified) return null;

  const message = hasEmail
    ? t('email:banner:verify_message', { email: desc.email })
    : t('email:banner:add_message');
  const cta = hasEmail
    ? t('email:banner:verify_cta')
    : t('email:banner:add_cta');
  const onCta = hasEmail
    ? () => setOpenVerify(true)
    : () => setOpenChange(true);
  const icon = hasEmail ? 'mark_email_unread' : 'alternate_email';

  return (
    <>
      <FlexRow flex={0} className={cs.bannerWarm + ' gap-[12px]'}>
        <span className="material-icons text-[20px] !text-[#8A5028]">{icon}</span>
        <span className="text-[13px] flex-1 leading-[1.4]">{message}</span>
        <span className={cs.bannerLink} onClick={onCta}>{cta}</span>
        <span className="material-icons text-[18px] cursor-pointer !text-[#8A5028]/60 hover:!text-[#8A5028]"
              title={t('modals:buttons:dismiss')}
              onClick={() => setDismissed(true)}>
          close
        </span>
      </FlexRow>
      {openVerify &&
        <VerifyEmailModal onClose={() => setOpenVerify(false)} />
      }
      {openChange &&
        <ChangeEmailModal onClose={() => setOpenChange(false)} />
      }
    </>
  );
};

export default EmailBanner;
